import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { appTitle } from '@/shared/config/app-config'
import { Button } from '@/shared/ui/button'

export function StartupFallback({ error }: { error: unknown }) {
  const { t } = useTranslation()
  const content = getFallbackContent(error)

  return (
    <main className="min-h-screen bg-radial-[at_top] from-accent/50 via-background to-background px-6 py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-3xl items-center justify-center">
        <section className="w-full rounded-[2rem] border border-border/70 bg-background/90 p-8 shadow-2xl shadow-primary/10 backdrop-blur sm:p-10">
          <div className="mb-6 flex size-14 items-center justify-center rounded-2xl bg-muted text-primary">
            {content.isOffline ? (
              <WifiOff className="size-7" />
            ) : (
              <AlertTriangle className="size-7" />
            )}
          </div>
          <div className="space-y-4">
            <p className="text-sm font-semibold uppercase tracking-[0.32em] text-primary">
              {appTitle}
            </p>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              {content.title}
            </h1>
            <p className="text-base leading-7 text-muted-foreground sm:text-lg">
              {content.description}
            </p>
            {content.detail ? (
              <p className="rounded-xl border border-border/70 bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
                {content.detail}
              </p>
            ) : null}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => window.location.reload()}>
              <RefreshCw className="size-4" />
              {t('shell:retry')}
            </Button>
          </div>
        </section>
      </div>
    </main>
  )
}

function getFallbackContent(error: unknown) {
  const message = error instanceof Error ? error.message : null
  const isOffline =
    typeof navigator !== 'undefined' &&
    !navigator.onLine &&
    isNetworkRequiredStartupError(message)

  if (isOffline) {
    return {
      isOffline: true,
      title: 'Network required for workspace data',
      description:
        'The app shell can be installed and cached, but loading your workspace still depends on the live GraphQL and document database services.',
      detail:
        'Reconnect to the network and retry. Offline document browsing and editing are not part of this version.',
    }
  }

  return {
    isOffline: false,
    title: `Unable to start ${appTitle}`,
    description:
      'The app could not complete startup. Retry the page, then check environment and backend connectivity if the problem persists.',
    detail: message,
  }
}

function isNetworkRequiredStartupError(message: string | null) {
  if (!message) {
    return false
  }

  return (
    message.includes('no active workspace configured') ||
    message.includes('Unable to initialize records')
  )
}
